import { BCContextMenu, findMessage, IContextMenuClickEventArgs } from '@bimser/components';
import { IItem, isImageFile, getIconByExtension } from '@bimser/core';
import IconDelete from '@bimser/icons/16/delete';
import IconDownload from '@bimser/icons/16/download';
import IconMore from '@bimser/icons/16/more';
import IconMore2 from '@bimser/icons/16/more2';
import IconRename from '@bimser/icons/16/rename';
import IconViewFile from '@bimser/icons/16/view-file';
import IconCompare from '@bimser/icons/16/comparison';
import IconWarning from '@bimser/icons/16/warning';
import dayjs from 'dayjs';
import * as React from 'react';
import Styles from '../../../../../assets/RDStyle.scss';
import {
	IRelatedDocumentFile,
	IRelatedDocumentsOptions,
	RelatedDocumentsViewType
} from '../entities';
import { getDownloadUrl } from '../helper/getAllowedExtensionsOptions';
import RDPopover from './RDPopover';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(localizedFormat);
import classNames from 'classnames/bind';
const cx = classNames.bind(Styles);

class RDFile extends React.PureComponent<RDFileProps, any> {
	alreadyClosed = true;
	ref: React.RefObject<HTMLDivElement>;

	constructor(props: RDFileProps) {
		super(props);

		this.getThumbnail = this.getThumbnail.bind(this);
		this.onContextMenuClicked = this.onContextMenuClicked.bind(this);
		this.onContextMenuClose = this.onContextMenuClose.bind(this);
		this.onContextMenuOpen = this.onContextMenuOpen.bind(this);
		this.renderFileDescription = this.renderFileDescription.bind(this);
		this.renderFileOptions = this.renderFileOptions.bind(this);
		this.getOptionItems = this.getOptionItems.bind(this);
		this.onToggleSelected = this.onToggleSelected.bind(this);

		this.ref = React.createRef();

		this.state = {
			isOptionsVisible: false,
			mouseEvent: null
		};
	}

	onContextMenuOpen(e: React.MouseEvent) {
		e.persist();
		e.stopPropagation();
		if (this.alreadyClosed) {
			this.setState({ mouseEvent: e, isOptionsVisible: true });
			this.alreadyClosed = false;
		}
	}

	onContextMenuClose(visible: boolean) {
		this.alreadyClosed = true;
		this.setState({ mouseEvent: null, isOptionsVisible: false });
	}

	onContextMenuClicked(args: IContextMenuClickEventArgs) {
		this.setState({ mouseEvent: null, isOptionsVisible: false });
		this.alreadyClosed = false;

		if (this.props.onFileOptionClicked) {
			this.props.onFileOptionClicked(this.props.file.id, args.data.key);
		}
	}

	onToggleSelected() {
		const container = this.ref.current;

		if (container) {
			container.toggleAttribute('selected');
			this.props.onAddDownloadClick(
				this.props.file,
				container.hasAttribute('selected'),
				this.props.isComparable
			);
		}
	}

	getOptionItems(f: IRelatedDocumentFile) {
		const options: IItem[] = [
			{
				key: 'download',
				text: findMessage.get('101167'),
				icon: IconDownload.info,
				disabled: !f.path
			},
			{
				key: 'dv1',
				isDivider: true,
				disabled: !this.props.options.deleteEnabled || !f.path
			},
			{
				key: 'unlink',
				text: findMessage.get('101218'),
				icon: IconDelete.info,
				disabled: !this.props.options.deleteEnabled
			},
			{
				key: 'enterDescription',
				text: findMessage.get('101390'),
				icon: IconRename.info,
				disabled: this.props.options.readOnly || !f.path
			},
			{
				key: 'view',
				text: findMessage.get('101391'),
				icon: IconViewFile.info,
				disabled: !this.props.options.viewable || !f.path
			},
			{
				key: 'compare',
				text: findMessage.get('100171'),
				icon: IconCompare.info,
				disabled: !this.props.options.viewable || !this.props.isComparable || !f.path
			}
		];

		return options.filter(item => !item.disabled);
	}

	renderFileOptions(f: IRelatedDocumentFile) {
		const Icon = this.props.viewType == 'card' ? IconMore2 : IconMore;
		const items = this.getOptionItems(f);

		if (!items.every(item => item.disabled)) {
			return (
				<div className={Styles.FileOptionsWrapper}>
					<div
						className={Styles.FileOptions}
						onClick={this.onContextMenuOpen}
					>
						<BCContextMenu
							key={f.id + 'contextMenu'}
							className={Styles.OptionsMenu}
							visible={this.state.isOptionsVisible}
							items={items}
							mouseEvent={this.state.mouseEvent}
							onClose={this.onContextMenuClose}
							onClick={this.onContextMenuClicked}
							closeWhenAnyScroll
							popupContainer={this.props.options.fileOptionsPopupContainer}
						/>
						<Icon />
					</div>
				</div>
			);
		}
	}

	renderFileDescription(f: IRelatedDocumentFile) {
		if (!this.props.options.showFileDescription) {
			return null;
		} else if (
			this.props.options.fileDescriptionIsRequired &&
			(f.description == null || f.description.length == 0)
		) {
			return (
				<div className={Styles.DescriptionWarning}>
					<IconWarning />
					<span>{findMessage.get('101389')}</span>
				</div>
			);
		} else {
			return (
				<span
					className={Styles.FileDescription}
					title={f.description || ''}
				>{`${this.props.viewType == 'list' ? ' - ' : ''}${f.description || ''}`}</span>
			);
		}
	}

	renderPreview(data: any) {
		return <img src={data} />;
	}

	getThumbnail() {
		if (
			this.props.options.showContentForImageFiles &&
			isImageFile(this.props.file.extension) &&
			((this.props.file.data && typeof this.props.file.data == 'string') ||
				this.props.file.downloadUrl)
		) {
			const source =
				this.props.file.data && typeof this.props.file.data == 'string'
					? this.props.file.data
					: getDownloadUrl(this.props.file.downloadUrl);

			return (
				<RDPopover
					key="Preview"
					content={this.renderPreview(source)}
					title={findMessage.get('100703')}
					overlayClassName={Styles.PreviewPopover}
					placement="bottomLeft"
					trigger="click"
					expandable
				>
					<span
						className={cx({
							Extension: true,
							Previewable: this.props.viewType == RelatedDocumentsViewType.Compact
						})}
					>
						{this.props.viewType == RelatedDocumentsViewType.Compact ? (
							getIconByExtension(this.props.file.extension)
						) : (
							<img src={source} />
						)}
					</span>
				</RDPopover>
			);
		} else if (this.props.options.showFileExtension) {
			return (
				<span className={Styles.Extension}>
					{getIconByExtension(this.props.file.extension)}
				</span>
			);
		}
	}

	unknownValidation(text: string) {
		return text || findMessage.get('100810');
	}

	render() {
		if (this.props.viewType === 'list') {
			return (
				<div
					ref={this.ref}
					className={Styles.RelatedDocumentFile}
					key={this.props.file.key}
					onClick={this.onToggleSelected}
				>
					{this.getThumbnail()}
					<div className={Styles.FileInfo}>
						<div className={Styles.FileTitle}>
							<span
								className={Styles.FileName}
								title={this.props.file.name}
							>
								{this.unknownValidation(this.props.file.name)}
							</span>
							{this.renderFileDescription(this.props.file)}
						</div>
						{this.props.options.showPath && (
							<span
								className={Styles.FilePath}
								title={this.props.file.path}
							>
								{this.unknownValidation(this.props.file.path)}
							</span>
						)}
					</div>
					{this.props.options.showCreator && (
						<div className={Styles.FileCreator}>
							<span className={Styles.FilePropertyText}>
								{this.unknownValidation(this.props.file.creator)}
							</span>
						</div>
					)}
					{this.renderFileOptions(this.props.file)}
					{this.props.options.showCreateDate && (
						<div className={Styles.FileCreatedDate}>
							<span
								className={Styles.FilePropertyText}
								title={dayjs(this.props.file.createDate).format('L LTS')}
							>
								{dayjs(this.props.file.createDate).format('ll')}
							</span>
						</div>
					)}
				</div>
			);
		} else if (this.props.viewType === 'card') {
			return (
				<div
					ref={this.ref}
					className={Styles.RelatedDocumentFile}
					key={this.props.file.key}
					onClick={this.onToggleSelected}
				>
					{this.getThumbnail()}
					<div className={Styles.CardFileContent}>
						{this.renderFileOptions(this.props.file)}
						<div className={Styles.FileTitle}>
							<span
								className={Styles.FileName}
								title={this.props.file.name}
							>
								{this.unknownValidation(this.props.file.name)}
							</span>
							{this.renderFileDescription(this.props.file)}
						</div>
						{this.props.options.showPath && (
							<span
								className={Styles.FilePath}
								title={this.props.file.path}
							>
								{this.unknownValidation(this.props.file.path)}
							</span>
						)}
						{this.props.options.showCreator && (
							<div className={Styles.FileCreator}>
								<span className={Styles.FilePropertyText}>
									{this.unknownValidation(this.props.file.creator)}
								</span>
							</div>
						)}
						{this.props.options.showCreateDate && (
							<div className={Styles.FileCreatedDate}>
								<span
									className={Styles.FilePropertyText}
									title={dayjs(this.props.file.createDate).format('L LTS')}
								>
									{dayjs(this.props.file.createDate).format('ll')}
								</span>
							</div>
						)}
					</div>
				</div>
			);
		} else {
			return (
				<div
					className={Styles.RelatedDocumentFile}
					key={this.props.file.key}
				>
					{this.getThumbnail()}
					<span
						className={Styles.FileName}
						title={this.props.file.name}
					>
						{this.props.file.name}
					</span>
					{this.renderFileOptions(this.props.file)}
				</div>
			);
		}
	}
}

export interface RDFileProps {
	file: IRelatedDocumentFile;
	options: IRelatedDocumentsOptions;
	viewType: RelatedDocumentsViewType;
	isComparable: boolean;

	onFileOptionClicked: (fileId: string, eventKey: string) => void;
	onAddDownloadClick: (file: any, val: boolean, comparable: boolean) => void;
}

export default RDFile;
